import React, { useState, useEffect, useContext } from 'react';
import { CampaignProps } from './types/propTypes';
import { CreditCardIcon, PlusCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useHttp } from 'src/hooks';
import { AuthContext } from 'src/contexts';
import IconSvg from 'src/components/common/ui/IconSvg';

interface PaymentStatus {
  adAccountId: string;
  hasPaymentMethod: boolean;
  paymentDetails?: {
    id: string;
    display_string: string;
    type: number;
  };
}

const PaymentForm: React.FC<CampaignProps> = ({ setCampaignData, campaignData, setIsStepValid }) => {
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [popupWindow, setPopupWindow] = useState<Window | null>(null);
  const { sendRequest, isLoading } = useHttp();
  const { accessToken } = useContext(AuthContext);

  useEffect(() => {
    if (setIsStepValid) {
      setIsStepValid(paymentStatus?.hasPaymentMethod || false);
    }
  }, [paymentStatus, setIsStepValid]);
  // Fetch payment status
  const fetchPaymentStatus = async () => {
    try {
      sendRequest(
        {
          url: `ads/ad-account-payment-status/${campaignData.adAccountId}`,
          method: 'GET',
          accessToken
        },
        (data: any) => {
          setPaymentStatus(data);
          setCampaignData((prev: any) => ({
            ...prev,
            paymentMethod: data.hasPaymentMethod ? data.paymentDetails?.display_string : ''
          }));
        }
      );
    } catch (error) {
      console.error('Error fetching payment status:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPaymentStatus();
  };

  const handleAddPaymentMethod = () => {
    const fbBillingUrl = `https://business.facebook.com/ads/manager/account_settings/account_billing/?act=${paymentStatus?.adAccountId || campaignData.adAccountId}`;

    // Small popup window
    const newPopup = window.open(
      fbBillingUrl,
      'fbPaymentPopup',
      'width=900,height=700,left=200,top=100,scrollbars=yes,resizable=yes'
    );

    if (newPopup) {
      setPopupWindow(newPopup);
    }
  };

  // Check if popup window is closed
  useEffect(() => {
    if (!popupWindow) return;

    const checkPopupClosed = setInterval(() => {
      if (popupWindow.closed) {
        clearInterval(checkPopupClosed);
        setPopupWindow(null);
        // Fetch payment status when popup is closed
        fetchPaymentStatus();
      }
    }, 500); // Check every 500ms

    return () => {
      clearInterval(checkPopupClosed);
    };
  }, [popupWindow]);

  useEffect(() => {
    fetchPaymentStatus();
  }, []);

  const getCardType = (type: number) => {
    const cardTypes = {
      1: 'VISA',
      2: 'MasterCard',
      3: 'American Express',
      4: 'Discover',
      5: 'JCB',
      6: 'Diners Club'
    };
    return cardTypes[type as keyof typeof cardTypes] || 'Credit Card';
  };

  const getCardIcon = (type: number) => {
    const cardIcons = {
      1: <IconSvg icon="visa" />,
      2: <IconSvg icon='mastercard' />,
      3: <IconSvg icon='amex' />,
      4: <IconSvg icon='discover' />,
      5: <IconSvg icon='jcb' />,
      6: <IconSvg icon='diners' />
    };
    return cardIcons[type as keyof typeof cardIcons] || <IconSvg icon='defaultCard' />;
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Payment Method</h2>
          <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
        </div>

        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-6"></div>

          {/* Card Skeleton */}
          <div className="bg-gradient-to-r from-gray-200 to-gray-300 rounded-2xl p-6 h-44"></div>

          <div className="h-12 bg-gray-200 rounded-xl mt-6"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Payment Method</h2>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="p-2 text-gray-500 hover:text-blue-600 transition-colors duration-200 rounded-full hover:bg-blue-50"
          title="Refresh payment status"
        >
          <ArrowPathIcon className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {paymentStatus?.hasPaymentMethod ? (
        <div className="space-y-6">
          <p className="text-gray-600 mb-2">
            Your current payment method for Facebook Ads:
          </p>

          {/* Beautiful Card Display */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-500 rounded-2xl blur opacity-10 group-hover:opacity-20 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative bg-gradient-to-r from-blue-600 to-purple-700 rounded-2xl p-6 text-white shadow-lg overflow-hidden">
              <div className="absolute -right-6 -top-6 w-24 h-24 bg-white/10 rounded-full"></div>
              <div className="absolute -right-12 -top-12 w-32 h-32 bg-white/5 rounded-full"></div>

              <div className="flex justify-between items-start mb-8 relative z-10">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center">
                  {getCardIcon(paymentStatus.paymentDetails?.type || 1)}
                </div>
                <div className="text-xs bg-white/20 rounded-full px-3 py-1.5 font-medium">
                  {getCardType(paymentStatus.paymentDetails?.type || 1)}
                </div>
              </div>

              <div className="mb-6 relative z-10">
                <div className="text-sm opacity-90 mb-1">Card Number</div>
                <div className="text-xl font-semibold tracking-wider">
                  {paymentStatus.paymentDetails?.display_string}
                </div>
              </div>

              <div className="flex justify-between items-center relative z-10">
                <div>
                  <div className="text-sm opacity-90 mb-1">Status</div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                    <span className="text-green-300 font-medium">Active</span>
                  </div>
                </div>
                <div className="text-xs opacity-70 bg-black/20 rounded-lg px-2 py-1">
                  Account: {paymentStatus.adAccountId.slice(0, 4)}...{paymentStatus.adAccountId.slice(-4)}
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleAddPaymentMethod}
            className="w-full mt-2 bg-white border border-gray-200 hover:border-blue-300 text-gray-700 font-medium py-3.5 px-4 rounded-xl transition-all duration-200 hover:shadow-sm flex items-center justify-center hover:text-blue-600"
          >
            <PlusCircleIcon className="w-5 h-5 mr-2" />
            Change Payment Method
          </button>
        </div>
      ) : (
        <div className="text-center py-8 px-4">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-50 rounded-2xl mb-5">
            <CreditCardIcon className="w-10 h-10 text-blue-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            No Payment Method Added
          </h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Add a payment method to start running your Facebook ads campaign. Your payment information is securely processed by Facebook.
          </p>

          <button
            onClick={handleAddPaymentMethod}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3.5 px-8 rounded-xl transition-all duration-200 hover:shadow-md flex items-center justify-center mx-auto"
          >
            <PlusCircleIcon className="w-5 h-5 mr-2" />
            Add Payment Method
          </button>

          <p className="text-sm text-gray-400 mt-5">
            You&apos;ll be redirected to Facebook&apos;s secure payment setup
          </p>

        </div>
      )}
    </div>
  );
};

export default PaymentForm;